<script setup lang="ts">
import { ref, reactive, onMounted, watch, computed } from 'vue';
import useDashboardStore from '@/stores/Dashboard/dashboard-store';
import { DashboardWidget } from '@/interfaces/Dashboard';
import debounce from 'lodash/debounce';
import ConnxSkeletonExpiredLicencesCount from './ConnxSkeletonExpiredLicencesCount.vue';
import ConnxDashboardWidgetNoData from './ConnxDashboardWidgetNoData.vue';
import ConnxStatusCard from '@/components/Atoms/ConnxStatusCard.vue';
import type { Status } from '@/interfaces/Dashboard';
import ConnxStatusRowCard from '@/components/Atoms/ConnxStatusRowCard.vue';
import { useElementSize } from '@vueuse/core';

const props = defineProps<{ height: number; type: string; widget: DashboardWidget }>();

interface ExpirySummaryData {
  total: number | null;
  title: string | null;
  label: string | null;
  series: Array<{ name: string; data: number }>;
}

const expiryData = reactive<ExpirySummaryData>({
  total: null,
  title: null,
  label: null,
  series: [],
});

const badgeText = props.widget?.defaultJsonProperties?.badgeText;

const currentBadgeText = computed(() => {
  if (badgeText) {
    switch (status.value) {
      case 'success':
        return badgeText.success ?? badgeText.default;
      case 'warning':
        return badgeText.warning ?? badgeText.default;
      case 'danger':
        return badgeText.danger ?? badgeText.default;
      default:
        return badgeText.default;
    }
  }
});

const containerRef = ref<HTMLElement | null>(null);
const cardRef = ref<HTMLElement | null>(null);

const { height: containerHeight } = useElementSize(containerRef);
const { height: contentheight } = useElementSize(cardRef);

const availableHeight = computed(() => {
  const TITLE_PADDING = 20;
  if (listOnRight.value) {
    return Math.max(contentheight.value + TITLE_PADDING, 0);
  }
  return Math.max(containerHeight.value - contentheight.value - TITLE_PADDING, 0);
});

const total = ref<number>(0);
const series = ref<any[] | null>(null);
const loading = ref(true);

const dashboardStore = useDashboardStore();

const status = computed<Status>(() => {
  const benchmarks = props.widget.defaultJsonProperties?.parameters?.benchmarks?.values;
  const totalValue = expiryData.total;

  if (!benchmarks || benchmarks.length < 2 || totalValue === null) {
    return 'danger';
  }

  const [benchmarkFirst, benchmarkSecond] = benchmarks;

  if (totalValue <= benchmarkFirst) return 'success';
  if (totalValue <= benchmarkSecond) return 'warning';

  return 'danger';
});

const showList = computed(() => {
  if (!props.widget) return false;
  if (props.widget.defaultJsonProperties.h <= 3 && props.widget.defaultJsonProperties.w <= 4) {
    return false;
  }
  if (expiryData.series.length === 0) {
    return false;
  }
  return true;
});

const formatData = (data: any) => {
  if (!data || !data.series) {
    expiryData.total = null;
    expiryData.title = null;
    expiryData.label = null;
    expiryData.series = [];
    return;
  }
  if ('nonCompliantPercent' in data) {
    expiryData.total = data.nonCompliantPercent;
    expiryData.title = `${data.nonCompliantPercent}%`;
    expiryData.label = `${data.nonCompliantCount} of ${data.totalTerminated} employees`;
  } else {
    expiryData.total = data.total;
    expiryData.title = `${data.total}`;
    expiryData.label = null;
  }
  expiryData.series = data.series;
};

const listOnRight = computed(() => {
  if (!props.widget) return false;
  return props.widget.defaultJsonProperties.w > 4;
});

const fetchData = async () => {
  try {
    const data = await dashboardStore.fetchDataSource(props.widget.dataSourceGuid, {
      widgetGuid: props.widget.dashboardTabWidgetGuid,
    });
    if (data?.data) {
      formatData(data.data);
    }
  } catch (error) {
    console.error('Failed to fetch data:', error);
  } finally {
    loading.value = false;
  }
};

const debouncedFetchData = debounce(async () => {
  await fetchData();
}, 600);

watch(
  () => {
    const p = props.widget.defaultJsonProperties?.parameters;
    return {
      filterByValues: p?.filterBy?.map((f) => f.value) ?? [],
      benchmarkValues: p?.benchmarks?.values,
    };
  },
  async () => {
    await debouncedFetchData();
  },
  { deep: false }
);
onMounted(fetchData);
</script>

<template>
  <ConnxSkeletonExpiredLicencesCount v-if="loading" />

  <div
    ref="containerRef"
    class="tw-w-full tw-h-full tw-p-0"
    :class="listOnRight ? 'tw-flex tw-flex-row tw-gap-6' : 'tw-flex tw-flex-col'"
  >
    <!-- Big Card -->
    <ConnxStatusCard
      ref="cardRef"
      :title="expiryData.title"
      :status="status"
      :badge-text="currentBadgeText"
      :label-text="expiryData.label"
      class="h-full"
    />

    <h4
      v-if="badgeText && showList && !listOnRight"
      class="tw-text-base tw-mb-2 tw-text-connx-grey-shade-55 tw-font-semibold"
      >{{ badgeText.title }}</h4
    >
    <div
      v-if="showList && badgeText"
      :class="{
        'tw-flex tw-flex-col tw-items-stretch tw-justify-center tw-grow': listOnRight,
      }"
    >
      <h4 v-if="listOnRight" class="tw-text-base tw-mb-2 tw-text-connx-grey-shade-55 tw-font-semibold">{{
        badgeText.title
      }}</h4>
      <div class="ScrollBar ScrollBar--vertical">
        <div :style="{ maxHeight: `${availableHeight}px` }">
          <div class="tw-flex tw-flex-col tw-gap-2">
            <ConnxStatusRowCard
              :status="status"
              v-for="(item, index) in expiryData.series"
              :key="index"
              :title="item.name"
              :value="item.data"
            ></ConnxStatusRowCard>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
